import React, { useState } from 'react';
import api from '../../config/api';
import toast from 'react-hot-toast';
import { 
  TruckIcon, 
  // SparklesIcon, // Unused - removed
  WrenchScrewdriverIcon,
  BoltIcon,
  CogIcon,
  HomeIcon,
  CurrencyDollarIcon,
  ShieldCheckIcon,
  CalendarDaysIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';
import ImageUpload from './ImageUpload';
import DataBrowser from '../dashboard/DataBrowser';
import VesselSeasonsTab from './VesselSeasonsTab';

const ComprehensiveVesselForm = ({ formData, setFormData, onSubmit, onCancel, title = "Add New Vessel" }) => {
  const [activeSection, setActiveSection] = useState('basic');
  const [showHoursBrowser, setShowHoursBrowser] = useState(false);
  const [hoursBrowserEngineIndex, setHoursBrowserEngineIndex] = useState(null);
  const [showSpecsModal, setShowSpecsModal] = useState(false);
  const [specsModalEngineIndex, setSpecsModalEngineIndex] = useState(null);
  const [enginePartQueries, setEnginePartQueries] = useState({});
  
  // Ensure engines array exists for card UI
  const ensureEnginesArray = () => {
    if (!Array.isArray(formData.engines)) {
      setFormData(prev => ({ ...prev, engines: [] }));
    }
  };

  const sections = [
    { id: 'basic', name: 'Basic Info', icon: TruckIcon },
    { id: 'physical', name: 'Physical Specs', icon: HomeIcon },
    { id: 'electrical', name: 'Electrical', icon: BoltIcon },
    { id: 'engine', name: 'Engines', icon: CogIcon },
    { id: 'details', name: 'Details', icon: DocumentTextIcon },
    { id: 'ownership', name: 'Ownership', icon: CurrencyDollarIcon },
    { id: 'service', name: 'Service', icon: ShieldCheckIcon },
    { id: 'dates', name: 'Important Dates', icon: CalendarDaysIcon },
    { id: 'seasons', name: 'Seasons', icon: CalendarDaysIcon }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const validateSignalKConfig = () => {
    if (!formData.signalk_host) {
      return { isValid: false, message: 'Server address is required' };
    }
    
    const port = formData.signalk_port || 3000;
    if (port < 1 || port > 65535) {
      return { isValid: false, message: 'Port must be between 1 and 65535' };
    }
    
    // Basic hostname/IP validation
    const hostPattern = /^[a-zA-Z0-9.-]+$/;
    if (!hostPattern.test(formData.signalk_host)) {
      return { isValid: false, message: 'Invalid server address format' };
    }
    
    return { isValid: true, message: 'Valid SignalK configuration' };
  };

  const testSignalKConnection = async () => {
    const validation = validateSignalKConfig();
    
    if (!validation.isValid) {
      toast.error(`Invalid configuration: ${validation.message}`);
      return;
    }

    try {
      // Extract connection details from form fields
      const host = formData.signalk_host;
      const port = formData.signalk_port || 3000;
      const ssl = formData.signalk_ssl || false;
      const username = formData.signalk_username;
      const password = formData.signalk_password;

      console.log('Testing SignalK connection...', {
        host,
        port,
        ssl,
        username: username ? '[PROVIDED]' : '[NOT PROVIDED]'
      });

      const loadingToast = toast.loading('🔍 Testing SignalK connection...');

      const response = await api.post('/api/signalk/test-connection', {
        host: host,
        port: parseInt(port),
        ssl: ssl,
        username: username || undefined,
        password: password || undefined
      });

      toast.dismiss(loadingToast);

      if (response.data.success) {
        let message = 'SignalK connection successful!';
        if (response.data.pgnAnalysis && response.data.pgnAnalysis.detectedPGNs.length > 0) {
          message += ` Found ${response.data.pgnAnalysis.detectedPGNs.length} PGNs across ${response.data.pgnAnalysis.categories.length} system categories.`;
        }
        toast.success(message, { duration: 6000 });
        
        // Show detailed results in console for debugging
        console.log('SignalK test results:', response.data);
      } else {
        toast.error(`SignalK connection failed: ${response.data.message}`);
        console.error('SignalK test failed:', response.data);
      }
      
    } catch (error) {
      console.error('Error testing SignalK connection:', error);
      toast.error(`Connection test failed: ${error.response?.data?.message || error.message}`);
    }
  };

  const renderBasicInfo = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-medium" style={{color: '#f8fafc'}}>Basic Information</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-2" style={{color: '#cbd5e1'}}>Vessel Name *</label>
          <input
            type="text"
            value={formData.vessel_name || ''}
            onChange={(e) => handleInputChange('vessel_name', e.target.value)}
            className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2"
            style={{borderColor: '#475569', backgroundColor: '#334155', color: '#f8fafc', focusRingColor: '#3b82f6'}}
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-2" style={{color: '#cbd5e1'}}>Vessel Type *</label>
          <select
            value={formData.vessel_type || 'sailboat'}
            onChange={(e) => handleInputChange('vessel_type', e.target.value)}
            className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2"
            style={{borderColor: '#475569', backgroundColor: '#334155', color: '#f8fafc', focusRingColor: '#3b82f6'}}
          >
            <option value="sailboat">Sailboat</option>
            <option value="powerboat">Powerboat</option>
            <option value="yacht">Yacht</option>
            <option value="catamaran">Catamaran</option>
            <option value="motorboat">Motorboat</option>
            <option value="fishing_boat">Fishing Boat</option>
            <option value="tender">Tender</option>
            <option value="other">Other</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium mb-2" style={{color: '#cbd5e1'}}>Year Built *</label>
          <input
            type="number"
            value={formData.year_built || ''}
            onChange={(e) => handleInputChange('year_built', e.target.value)}
            className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2"
            style={{borderColor: '#475569', backgroundColor: '#334155', color: '#f8fafc', focusRingColor: '#3b82f6'}}
            min="1900"
            max="2030"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-2" style={{color: '#cbd5e1'}}>Manufacturer *</label>
          <input
            type="text"
            value={formData.manufacturer || ''}
            onChange={(e) => handleInputChange('manufacturer', e.target.value)}
            className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2"
            style={{borderColor: '#475569', backgroundColor: '#334155', color: '#f8fafc', focusRingColor: '#3b82f6'}}
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-2" style={{color: '#cbd5e1'}}>Model *</label>
          <input
            type="text"
            value={formData.model || ''}
            onChange={(e) => handleInputChange('model', e.target.value)}
            className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2"
            style={{borderColor: '#475569', backgroundColor: '#334155', color: '#f8fafc', focusRingColor: '#3b82f6'}}
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-2" style={{color: '#cbd5e1'}}>HIN (Hull ID)</label>
          <input
            type="text"
            value={formData.hin || ''}
            onChange={(e) => handleInputChange('hin', e.target.value)}
            className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2"
            style={{borderColor: '#475569', backgroundColor: '#334155', color: '#f8fafc', focusRingColor: '#3b82f6'}}
            placeholder="e.g., US123456789"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-2" style={{color: '#cbd5e1'}}>Registration Number</label>
          <input
            type="text"
            value={formData.registration_number || ''}
            onChange={(e) => handleInputChange('registration_number', e.target.value)}
            className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2"
            style={{borderColor: '#475569', backgroundColor: '#334155', color: '#f8fafc', focusRingColor: '#3b82f6'}}
          />
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium mb-2" style={{color: '#cbd5e1'}}>Vessel Images</label>
          <ImageUpload
            images={formData.image_urls || []}
            onImagesChange={(images) => handleInputChange('image_urls', images)}
            primaryImageUrl={formData.primary_image_url || ''}
            onPrimaryImageChange={(url) => handleInputChange('primary_image_url', url)}
            maxImages={10}
          />
        </div>
      </div>
    </div>
  );

  const renderPhysicalSpecs = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-medium" style={{color: '#f8fafc'}}>Physical Specifications</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-2" style={{color: '#cbd5e1'}}>Length Overall (ft)</label>
          <input
            type="number"
            step="0.1"
            value={formData.length_overall || ''}
            onChange={(e) => handleInputChange('length_overall', e.target.value)}
            className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2"
            style={{borderColor: '#475569', backgroundColor: '#334155', color: '#f8fafc', focusRingColor: '#3b82f6'}}
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-2" style={{color: '#cbd5e1'}}>Beam (ft)</label>
          <input
            type="number"
            step="0.1"
            value={formData.beam || ''}
            onChange={(e) => handleInputChange('beam', e.target.value)}
            className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2"
            style={{borderColor: '#475569', backgroundColor: '#334155', color: '#f8fafc', focusRingColor: '#3b82f6'}}
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-2" style={{color: '#cbd5e1'}}>Draft (ft)</label>
          <input
            type="number"
            step="0.1"
            value={formData.draft || ''}
            onChange={(e) => handleInputChange('draft', e.target.value)}
            className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2"
            style={{borderColor: '#475569', backgroundColor: '#334155', color: '#f8fafc', focusRingColor: '#3b82f6'}}
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-2" style={{color: '#cbd5e1'}}>Displacement (lbs)</label>
          <input
            type="number"
            value={formData.displacement || ''}
            onChange={(e) => handleInputChange('displacement', e.target.value)}
            className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2"
            style={{borderColor: '#475569', backgroundColor: '#334155', color: '#f8fafc', focusRingColor: '#3b82f6'}}
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-2" style={{color: '#cbd5e1'}}>Fuel Capacity (gal)</label>
          <input
            type="number"
            value={formData.fuel_capacity || ''}
            onChange={(e) => handleInputChange('fuel_capacity', e.target.value)}
            className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2"
            style={{borderColor: '#475569', backgroundColor: '#334155', color: '#f8fafc', focusRingColor: '#3b82f6'}}
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-2" style={{color: '#cbd5e1'}}>Water Capacity (gal)</label>
          <input
            type="number"
            value={formData.fresh_water_capacity || ''}
            onChange={(e) => handleInputChange('fresh_water_capacity', e.target.value)}
            className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2"
            style={{borderColor: '#475569', backgroundColor: '#334155', color: '#f8fafc', focusRingColor: '#3b82f6'}}
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-2" style={{color: '#cbd5e1'}}>Holding Tank (gal)</label>
          <input
            type="number"
            value={formData.holding_tank_capacity || ''}
            onChange={(e) => handleInputChange('holding_tank_capacity', e.target.value)}
            className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2"
            style={{borderColor: '#475569', backgroundColor: '#334155', color: '#f8fafc', focusRingColor: '#3b82f6'}}
          />
        </div>
      </div>
    </div>
  );

  const renderElectrical = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-medium" style={{color: '#f8fafc'}}>Electrical System</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-2" style={{color: '#cbd5e1'}}>Electrical System (V)</label>
          <select
            value={formData.electrical_system || ''}
            onChange={(e) => handleInputChange('electrical_system', e.target.value)}
            className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2"
            style={{borderColor: '#475569', backgroundColor: '#334155', color: '#f8fafc', focusRingColor: '#3b82f6'}}
          >
            <option value="">Select System</option>
            <option value="12V">12V</option>
            <option value="24V">24V</option>
            <option value="110V">110V</option>
            <option value="220V">220V</option>
            <option value="12V/110V">12V/110V</option>
            <option value="24V/220V">24V/220V</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium mb-2" style={{color: '#cbd5e1'}}>Battery Bank (Ah)</label>
          <input
            type="number"
            value={formData.battery_bank_capacity || ''}
            onChange={(e) => handleInputChange('battery_bank_capacity', e.target.value)}
            className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2"
            style={{borderColor: '#475569', backgroundColor: '#334155', color: '#f8fafc', focusRingColor: '#3b82f6'}}
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-2" style={{color: '#cbd5e1'}}>Inverter (W)</label>
          <input
            type="number"
            value={formData.inverter_capacity || ''}
            onChange={(e) => handleInputChange('inverter_capacity', e.target.value)}
            className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2"
            style={{borderColor: '#475569', backgroundColor: '#334155', color: '#f8fafc', focusRingColor: '#3b82f6'}}
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-2" style={{color: '#cbd5e1'}}>Solar Panels (W)</label>
          <input
            type="number"
            value={formData.solar_panel_capacity || ''}
            onChange={(e) => handleInputChange('solar_panel_capacity', e.target.value)}
            className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2"
            style={{borderColor: '#475569', backgroundColor: '#334155', color: '#f8fafc', focusRingColor: '#3b82f6'}}
          />
        </div>
      </div>
    </div>
  );

  const addEngineCard = () => {
    ensureEnginesArray();
    setFormData(prev => ({
      ...prev,
      engines: [
        ...(Array.isArray(prev.engines) ? prev.engines : []),
        {
          id: Date.now(),
          name: `Engine ${((prev.engines || []).length + 1)}`,
          make: '',
          model: '',
          year: '',
          serial: '',
          hours: '',
          horsepower: '',
          fuel_type: '',
          type: 'main' // main | auxiliary | trolling
        }
      ]
    }));
  };

  const cloneEngineCard = (idx) => {
    setFormData(prev => ({
      ...prev,
      engines: prev.engines.map((e, i) => i === idx ? e : e).concat({
        ...prev.engines[idx],
        id: Date.now(),
        name: `${prev.engines[idx].name} (Copy)`
      })
    }));
  };

  const removeEngineCard = (idx) => {
    setFormData(prev => ({
      ...prev,
      engines: (prev.engines || []).filter((_, i) => i !== idx)
    }));
  };

  const updateEngineField = (idx, field, value) => {
    setFormData(prev => ({
      ...prev,
      engines: (prev.engines || []).map((e, i) => i === idx ? { ...e, [field]: value } : e)
    }));
  };

  const renderEngine = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-medium" style={{color: '#f8fafc'}}>Engines</h3>
      <div className="space-y-3">
        {(formData.engines || []).map((engine, idx) => (
          <div key={engine.id || idx} className="p-4 rounded-lg" style={{backgroundColor: '#0b1220', border: '1px solid #334155'}}>
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm font-medium" style={{color: '#93c5fd'}}>{engine.name || `Engine ${idx + 1}`}</div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      const loading = toast.loading('🔎 Getting engine specs...');
                      let resp;
                      if (formData.id) {
                        resp = await api.post(`/api/vessels/${formData.id}/engines/${idx}/specs`);
                      } else {
                        // Stateless fetch before vessel is saved
                        const payloadEngine = { ...engine };
                        resp = await api.post(`/api/vessels/engines/specs`, { engine: payloadEngine });
                      }
                      toast.dismiss(loading);
                      if (resp.data?.success) {
                        toast.success('✅ Engine specs updated');
                        setFormData(prev => {
                          const next = { ...prev };
                          next.engines = [...(prev.engines || [])];
                          const current = { ...(next.engines[idx] || {}) };
                          const specs = resp.data.specs || {};
                          current.specs = specs;
                          // Auto-fill missing fields
                          try {
                            const basic = specs.basic || {};
                            const power = specs.power || {};
                            if (!current.horsepower && typeof power.hp === 'number') current.horsepower = power.hp;
                            if (!current.fuel_type && typeof basic.fuel === 'string') current.fuel_type = basic.fuel.toLowerCase();
                            if (!current.year && (typeof basic.year === 'number' || typeof basic.year === 'string')) current.year = basic.year;
                            if (!current.make && typeof basic.make === 'string') current.make = basic.make;
                            if (!current.model && typeof basic.model === 'string') current.model = basic.model;
                            if (!current.serial && typeof basic.serial === 'string') current.serial = basic.serial;
                          } catch (_) {}
                          next.engines[idx] = current;
                          return next;
                        });
                      } else {
                        toast.error(resp.data?.error || 'Failed to get specs');
                      }
                    } catch (err) {
                        toast.error(err.response?.data?.error || err.message || 'Failed to get specs');
                    }
                  }}
                  className="text-xs px-2 py-1"
                  style={{backgroundColor: '#10b981', color: 'white', borderRadius: '6px'}}
                >Get Specs</button>
                {engine.specs && (
                  <button
                    type="button"
                    onClick={() => { setSpecsModalEngineIndex(idx); setShowSpecsModal(true); }}
                    className="text-xs px-2 py-1"
                    style={{backgroundColor: '#334155', color: 'white', borderRadius: '6px'}}
                  >View Specs</button>
                )}
                <button type="button" onClick={() => cloneEngineCard(idx)} className="text-xs px-2 py-1" style={{backgroundColor: '#3b82f6', color: 'white', borderRadius: '6px'}}>Clone</button>
                <button type="button" onClick={() => removeEngineCard(idx)} className="text-xs px-2 py-1" style={{backgroundColor: '#ef4444', color: 'white', borderRadius: '6px'}}>Delete</button>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2" style={{color: '#cbd5e1'}}>Name</label>
                <input type="text" value={engine.name || ''} onChange={(e) => updateEngineField(idx, 'name', e.target.value)} className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2" style={{borderColor: '#475569', backgroundColor: '#334155', color: '#f8fafc'}} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2" style={{color: '#cbd5e1'}}>Make</label>
                <input type="text" value={engine.make || ''} onChange={(e) => updateEngineField(idx, 'make', e.target.value)} className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2" style={{borderColor: '#475569', backgroundColor: '#334155', color: '#f8fafc'}} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2" style={{color: '#cbd5e1'}}>Model</label>
                <input type="text" value={engine.model || ''} onChange={(e) => updateEngineField(idx, 'model', e.target.value)} className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2" style={{borderColor: '#475569', backgroundColor: '#334155', color: '#f8fafc'}} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2" style={{color: '#cbd5e1'}}>Year</label>
                <input type="number" value={engine.year || ''} onChange={(e) => updateEngineField(idx, 'year', e.target.value)} className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2" style={{borderColor: '#475569', backgroundColor: '#334155', color: '#f8fafc'}} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2" style={{color: '#cbd5e1'}}>Serial</label>
                <input type="text" value={engine.serial || ''} onChange={(e) => updateEngineField(idx, 'serial', e.target.value)} className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2" style={{borderColor: '#475569', backgroundColor: '#334155', color: '#f8fafc'}} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2" style={{color: '#cbd5e1'}}>Hours</label>
                <input
                  type="number"
                  step="0.1"
                  value={engine.hours || ''}
                  onChange={(e) => updateEngineField(idx, 'hours', e.target.value)}
                  onBlur={(e) => {
                    const n = parseFloat(e.target.value);
                    if (!isNaN(n)) updateEngineField(idx, 'hours', Math.round(n * 10) / 10);
                  }}
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2"
                  style={{borderColor: '#475569', backgroundColor: '#334155', color: '#f8fafc'}}
                />
                <div className="mt-2">
                  <button
                    type="button"
                    onClick={() => {
                      if (!formData.id) {
                        toast('Save the vessel first to browse SignalK data for hours');
                        return;
                      }
                      setHoursBrowserEngineIndex(idx);
                      setShowHoursBrowser(true);
                    }}
                    className="px-3 py-1 text-xs rounded-md"
                    style={{backgroundColor: '#3b82f6', color: 'white'}}
                  >Browse SignalK</button>
                </div>
                {engine.hours_path && (
                  <div className="mt-1 text-xs" style={{color: '#9ca3af'}}>Linked to {engine.hours_path}</div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium mb-2" style={{color: '#cbd5e1'}}>Horsepower</label>
                <input type="number" value={engine.horsepower || ''} onChange={(e) => updateEngineField(idx, 'horsepower', e.target.value)} className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2" style={{borderColor: '#475569', backgroundColor: '#334155', color: '#f8fafc'}} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2" style={{color: '#cbd5e1'}}>Fuel Type</label>
                <select value={engine.fuel_type || ''} onChange={(e) => updateEngineField(idx, 'fuel_type', e.target.value)} className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2" style={{borderColor: '#475569', backgroundColor: '#334155', color: '#f8fafc'}}>
                  <option value="">Select Fuel Type</option>
                  <option value="gasoline">Gasoline</option>
                  <option value="diesel">Diesel</option>
                  <option value="electric">Electric</option>
                  <option value="hybrid">Hybrid</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2" style={{color: '#cbd5e1'}}>Type</label>
                <select value={engine.type || 'main'} onChange={(e) => updateEngineField(idx, 'type', e.target.value)} className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2" style={{borderColor: '#475569', backgroundColor: '#334155', color: '#f8fafc'}}>
                  <option value="main">Main</option>
                  <option value="auxiliary">Auxiliary</option>
                  <option value="trolling">Trolling</option>
                </select>
              </div>
            </div>

            {/* Parts search */}
            <div className="mt-4 p-3 rounded" style={{backgroundColor: '#0a1626', border: '1px solid #1f2a44'}}>
              <div className="text-xs font-medium mb-2" style={{color: '#9ca3af'}}>Search Parts</div>
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="e.g., oil filter, impeller, spark plugs"
                  value={enginePartQueries[idx] || ''}
                  onChange={(e) => setEnginePartQueries(prev => ({ ...prev, [idx]: e.target.value }))}
                  onKeyDown={async (e) => {
                    if (e.key === 'Enter') {
                      const q = (enginePartQueries[idx] || '').trim();
                      if (!q) return;
                      try {
                        const loading = toast.loading('🔎 Searching parts...');
                        let resp;
                        if (formData.id) {
                          resp = await api.post(`/api/vessels/${formData.id}/engines/${idx}/parts-search`, { query: q });
                        } else {
                          const payloadEngine = { ...engine };
                          resp = await api.post(`/api/vessels/engines/parts-search`, { query: q, engine: payloadEngine });
                        }
                        toast.dismiss(loading);
                        if (resp.data?.success) {
                          const parts = resp.data.parts || [];
                          if (parts.length === 0) return toast('No parts found');
                          toast.success(`Found ${parts.length} parts`);
                          // Attach to engine temporary view under specs.parts
                          setFormData(prev => {
                            const next = { ...prev };
                            next.engines = [...(prev.engines || [])];
                            const cur = { ...(next.engines[idx] || {}) };
                            const specs = { ...(cur.specs || {}) };
                            specs.parts = parts;
                            cur.specs = specs;
                            if (Array.isArray(resp.data.history)) {
                              cur.parts_search_history = resp.data.history;
                            }
                            next.engines[idx] = cur;
                            return next;
                          });
                          setEnginePartQueries(prev => ({ ...prev, [idx]: '' }));
                        } else {
                          toast.error(resp.data?.error || 'Parts search failed');
                        }
                      } catch (err) {
                        toast.error(err.response?.data?.error || err.message || 'Parts search failed');
                      }
                    }
                  }}
                  className="flex-1 px-3 py-2 border rounded-md focus:outline-none focus:ring-2"
                  style={{borderColor: '#475569', backgroundColor: '#0b1220', color: '#f8fafc'}}
                />
                <button
                  type="button"
                  onClick={async () => {
                    const q = (enginePartQueries[idx] || '').trim();
                    if (!q) return;
                    try {
                      const loading = toast.loading('🔎 Searching parts...');
                      let resp;
                      if (formData.id) {
                        resp = await api.post(`/api/vessels/${formData.id}/engines/${idx}/parts-search`, { query: q });
                      } else {
                        const payloadEngine = { ...engine };
                        resp = await api.post(`/api/vessels/engines/parts-search`, { query: q, engine: payloadEngine });
                      }
                      toast.dismiss(loading);
                      if (resp.data?.success) {
                        const parts = resp.data.parts || [];
                        if (parts.length === 0) return toast('No parts found');
                        toast.success(`Found ${parts.length} parts`);
                        setFormData(prev => {
                          const next = { ...prev };
                          next.engines = [...(prev.engines || [])];
                          const cur = { ...(next.engines[idx] || {}) };
                          const specs = { ...(cur.specs || {}) };
                          specs.parts = parts;
                          cur.specs = specs;
                          if (Array.isArray(resp.data.history)) {
                            cur.parts_search_history = resp.data.history;
                          }
                          next.engines[idx] = cur;
                          return next;
                        });
                        setEnginePartQueries(prev => ({ ...prev, [idx]: '' }));
                      } else {
                        toast.error(resp.data?.error || 'Parts search failed');
                      }
                    } catch (err) {
                      toast.error(err.response?.data?.error || err.message || 'Parts search failed');
                    }
                  }}
                  className="px-3 py-2 text-xs rounded-md"
                  style={{backgroundColor: '#3b82f6', color: 'white'}}
                >Search</button>
              </div>
              {engine.specs?.parts && engine.specs.parts.length > 0 && (
                <div className="mt-3 text-xs" style={{color: '#cbd5e1'}}>
                  {engine.specs.parts.slice(0,5).map((p, i) => (
                    <div key={i} className="py-1 border-b" style={{borderColor: '#1f2a44'}}>
                      <div className="font-medium">
                        {p.url ? (
                          <a href={p.url} target="_blank" rel="noopener noreferrer" className="hover:underline" style={{color: '#60a5fa'}}>
                            {p.name}
                          </a>
                        ) : (
                          <span>{p.name}</span>
                        )}
                        {p.part_number ? ` (${p.part_number})` : ''}
                      </div>
                      <div className="opacity-75">{p.category}</div>
                    </div>
                  ))}
                </div>
              )}
              {Array.isArray(engine.parts_search_history) && engine.parts_search_history.length > 0 && (
                <div className="mt-3 text-xs" style={{color: '#9ca3af'}}>
                  <div className="mb-1 font-medium" style={{color: '#cbd5e1'}}>Recent Searches</div>
                  {engine.parts_search_history.slice(0,3).map((h, i) => (
                    <div key={i} className="mb-2">
                      <div className="opacity-90">“{h.query}” • {new Date(h.searched_at).toLocaleString()}</div>
                      <div className="mt-1">
                        {(h.parts || []).slice(0,3).map((p, j) => (
                          <span key={j} className="mr-2">
                            {p.url ? (
                              <a href={p.url} target="_blank" rel="noopener noreferrer" className="hover:underline" style={{color: '#60a5fa'}}>
                                {p.name}
                              </a>
                            ) : (
                              <span>{p.name}</span>
                            )}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      <div>
        <button type="button" onClick={addEngineCard} className="px-4 py-2 text-sm rounded-md" style={{backgroundColor: '#3b82f6', color: 'white'}}>Add Engine</button>
      </div>
    </div>
  );

  const renderDetails = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-medium" style={{color: '#f8fafc'}}>Additional Details</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-2" style={{color: '#cbd5e1'}}>Hull Material</label>
          <select
            value={formData.hull_material || ''}
            onChange={(e) => handleInputChange('hull_material', e.target.value)}
            className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2"
            style={{borderColor: '#475569', backgroundColor: '#334155', color: '#f8fafc', focusRingColor: '#3b82f6'}}
          >
            <option value="">Select Material</option>
            <option value="fiberglass">Fiberglass</option>
            <option value="aluminum">Aluminum</option>
            <option value="steel">Steel</option>
            <option value="wood">Wood</option>
            <option value="composite">Composite</option>
            <option value="carbon_fiber">Carbon Fiber</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium mb-2" style={{color: '#cbd5e1'}}>Deck Material</label>
          <select
            value={formData.deck_material || ''}
            onChange={(e) => handleInputChange('deck_material', e.target.value)}
            className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2"
            style={{borderColor: '#475569', backgroundColor: '#334155', color: '#f8fafc', focusRingColor: '#3b82f6'}}
          >
            <option value="">Select Material</option>
            <option value="fiberglass">Fiberglass</option>
            <option value="teak">Teak</option>
            <option value="composite">Composite</option>
            <option value="aluminum">Aluminum</option>
            <option value="steel">Steel</option>
          </select>
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium mb-2" style={{color: '#cbd5e1'}}>Interior Layout</label>
          <textarea
            value={formData.interior_layout || ''}
            onChange={(e) => handleInputChange('interior_layout', e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2"
            style={{borderColor: '#475569', backgroundColor: '#334155', color: '#f8fafc', focusRingColor: '#3b82f6'}}
            placeholder="Describe the interior layout, cabins, galley, etc."
          />
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium mb-2" style={{color: '#cbd5e1'}}>Navigation Equipment</label>
          <textarea
            value={formData.navigation_equipment || ''}
            onChange={(e) => handleInputChange('navigation_equipment', e.target.value)}
            rows={2}
            className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2"
            style={{borderColor: '#475569', backgroundColor: '#334155', color: '#f8fafc', focusRingColor: '#3b82f6'}}
            placeholder="GPS, radar, autopilot, etc."
          />
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium mb-2" style={{color: '#cbd5e1'}}>Safety Equipment</label>
          <textarea
            value={formData.safety_equipment || ''}
            onChange={(e) => handleInputChange('safety_equipment', e.target.value)}
            rows={2}
            className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2"
            style={{borderColor: '#475569', backgroundColor: '#334155', color: '#f8fafc', focusRingColor: '#3b82f6'}}
            placeholder="Life jackets, flares, EPIRB, etc."
          />
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium mb-2" style={{color: '#cbd5e1'}}>Entertainment Systems</label>
          <textarea
            value={formData.entertainment_systems || ''}
            onChange={(e) => handleInputChange('entertainment_systems', e.target.value)}
            rows={2}
            className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2"
            style={{borderColor: '#475569', backgroundColor: '#334155', color: '#f8fafc', focusRingColor: '#3b82f6'}}
            placeholder="Stereo, TV, speakers, etc."
          />
        </div>
      </div>
    </div>
  );

  const renderOwnership = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-medium" style={{color: '#f8fafc'}}>Ownership Information</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-2" style={{color: '#cbd5e1'}}>Purchase Date</label>
          <input
            type="date"
            value={formData.purchase_date || ''}
            onChange={(e) => handleInputChange('purchase_date', e.target.value)}
            className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2"
            style={{borderColor: '#475569', backgroundColor: '#334155', color: '#f8fafc', focusRingColor: '#3b82f6'}}
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-2" style={{color: '#cbd5e1'}}>Purchase Price ($)</label>
          <input
            type="number"
            step="0.01"
            value={formData.purchase_price || ''}
            onChange={(e) => handleInputChange('purchase_price', e.target.value)}
            className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2"
            style={{borderColor: '#475569', backgroundColor: '#334155', color: '#f8fafc', focusRingColor: '#3b82f6'}}
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-2" style={{color: '#cbd5e1'}}>Current Value ($)</label>
          <input
            type="number"
            step="0.01"
            value={formData.current_value || ''}
            onChange={(e) => handleInputChange('current_value', e.target.value)}
            className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2"
            style={{borderColor: '#475569', backgroundColor: '#334155', color: '#f8fafc', focusRingColor: '#3b82f6'}}
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-2" style={{color: '#cbd5e1'}}>Insurance Company</label>
          <input
            type="text"
            value={formData.insurance_company || ''}
            onChange={(e) => handleInputChange('insurance_company', e.target.value)}
            className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2"
            style={{borderColor: '#475569', backgroundColor: '#334155', color: '#f8fafc', focusRingColor: '#3b82f6'}}
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-2" style={{color: '#cbd5e1'}}>Insurance Policy Number</label>
          <input
            type="text"
            value={formData.insurance_policy_number || ''}
            onChange={(e) => handleInputChange('insurance_policy_number', e.target.value)}
            className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2"
            style={{borderColor: '#475569', backgroundColor: '#334155', color: '#f8fafc', focusRingColor: '#3b82f6'}}
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-2" style={{color: '#cbd5e1'}}>Insurance Expiry</label>
          <input
            type="date"
            value={formData.insurance_expiry || ''}
            onChange={(e) => handleInputChange('insurance_expiry', e.target.value)}
            className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2"
            style={{borderColor: '#475569', backgroundColor: '#334155', color: '#f8fafc', focusRingColor: '#3b82f6'}}
          />
        </div>
      </div>
    </div>
  );

  const renderService = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-medium" style={{color: '#f8fafc'}}>Service Information</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-2" style={{color: '#cbd5e1'}}>Service Provider</label>
          <input
            type="text"
            value={formData.service_provider || ''}
            onChange={(e) => handleInputChange('service_provider', e.target.value)}
            className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2"
            style={{borderColor: '#475569', backgroundColor: '#334155', color: '#f8fafc', focusRingColor: '#3b82f6'}}
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-2" style={{color: '#cbd5e1'}}>Marina Location</label>
          <input
            type="text"
            value={formData.marina_location || ''}
            onChange={(e) => handleInputChange('marina_location', e.target.value)}
            className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2"
            style={{borderColor: '#475569', backgroundColor: '#334155', color: '#f8fafc', focusRingColor: '#3b82f6'}}
          />
        </div>
      </div>
    </div>
  );

  const renderImportantDates = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-medium" style={{color: '#f8fafc'}}>Important Dates</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-2" style={{color: '#cbd5e1'}}>Last Survey Date</label>
          <input
            type="date"
            value={formData.last_survey_date || ''}
            onChange={(e) => handleInputChange('last_survey_date', e.target.value)}
            className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2"
            style={{borderColor: '#475569', backgroundColor: '#334155', color: '#f8fafc', focusRingColor: '#3b82f6'}}
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-2" style={{color: '#cbd5e1'}}>Next Survey Due</label>
          <input
            type="date"
            value={formData.next_survey_due || ''}
            onChange={(e) => handleInputChange('next_survey_due', e.target.value)}
            className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2"
            style={{borderColor: '#475569', backgroundColor: '#334155', color: '#f8fafc', focusRingColor: '#3b82f6'}}
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-2" style={{color: '#cbd5e1'}}>Last Haul Out</label>
          <input
            type="date"
            value={formData.last_haul_out || ''}
            onChange={(e) => handleInputChange('last_haul_out', e.target.value)}
            className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2"
            style={{borderColor: '#475569', backgroundColor: '#334155', color: '#f8fafc', focusRingColor: '#3b82f6'}}
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-2" style={{color: '#cbd5e1'}}>Next Haul Out Due</label>
          <input
            type="date"
            value={formData.next_haul_out_due || ''}
            onChange={(e) => handleInputChange('next_haul_out_due', e.target.value)}
            className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2"
            style={{borderColor: '#475569', backgroundColor: '#334155', color: '#f8fafc', focusRingColor: '#3b82f6'}}
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium mb-2" style={{color: '#cbd5e1'}}>Notes</label>
        <textarea
          value={formData.notes || ''}
          onChange={(e) => handleInputChange('notes', e.target.value)}
          rows={4}
          className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2"
          style={{borderColor: '#475569', backgroundColor: '#334155', color: '#f8fafc', focusRingColor: '#3b82f6'}}
          placeholder="Additional notes about the vessel..."
        />
      </div>


    </div>
  );

  const renderSeasons = () => {
    // If editing an existing vessel, show the VesselSeasonsTab component
    if (formData.id) {
      return <VesselSeasonsTab vesselId={formData.id} />;
    }
    
    // If creating a new vessel, show a message
    return (
      <div className="space-y-4">
        <div className="bg-blue-900/20 border border-blue-600 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <span className="text-blue-400 text-lg">ℹ️</span>
            <div>
              <h5 className="font-medium text-blue-200 mb-2">Season Management</h5>
              <p className="text-sm text-blue-300">
                Save the vessel first, then you can create and manage seasons.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderSignalK = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium" style={{color: '#f8fafc'}}>SignalK Network Management</h3>
          <p className="text-sm" style={{color: '#cbd5e1'}}>
            Configure multiple SignalK connections with automatic failover and intelligent routing.
          </p>
        </div>
        
        {/* Connection Status Indicator */}
        <div className="flex items-center gap-2">
          <div className={`w-3 h-3 rounded-full ${
            formData.signalk_enabled && formData.signalk_host 
              ? 'bg-green-500 animate-pulse' 
              : 'bg-gray-500'
          }`} />
          <span className="text-xs" style={{color: '#cbd5e1'}}>
            {formData.signalk_enabled && formData.signalk_host 
              ? 'Primary Configured' 
              : 'Not Configured'
            }
          </span>
        </div>
      </div>

      {/* Primary Connection - Local Network */}
      <div className="bg-blue-900/30 border border-blue-600 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-3">
          <span className="text-lg">🏠</span>
          <h4 className="font-medium text-blue-200">Primary Connection (Local Network)</h4>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium mb-2" style={{color: '#cbd5e1'}}>SignalK Server Address *</label>
            <input
              type="text"
              value={formData.signalk_host || ''}
              onChange={(e) => handleInputChange('signalk_host', e.target.value)}
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2"
              style={{borderColor: '#475569', backgroundColor: '#334155', color: '#f8fafc'}}
              placeholder="192.168.5.217"
            />
            <p className="text-xs mt-1" style={{color: '#94a3b8'}}>
              Your boat's local SignalK server IP address
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium mb-2" style={{color: '#cbd5e1'}}>Port</label>
            <input
              type="number"
              value={formData.signalk_port || 3000}
              onChange={(e) => handleInputChange('signalk_port', parseInt(e.target.value) || 3000)}
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2"
              style={{borderColor: '#475569', backgroundColor: '#334155', color: '#f8fafc'}}
              min="1"
              max="65535"
              placeholder="3000"
            />
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <div>
            <label className="block text-sm font-medium mb-2" style={{color: '#cbd5e1'}}>Username (optional)</label>
            <input
              type="text"
              value={formData.signalk_username || ''}
              onChange={(e) => handleInputChange('signalk_username', e.target.value)}
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2"
              style={{borderColor: '#475569', backgroundColor: '#334155', color: '#f8fafc'}}
              placeholder="Leave blank if not required"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2" style={{color: '#cbd5e1'}}>Password (optional)</label>
            <input
              type="password"
              value={formData.signalk_password || ''}
              onChange={(e) => handleInputChange('signalk_password', e.target.value)}
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2"
              style={{borderColor: '#475569', backgroundColor: '#334155', color: '#f8fafc'}}
              placeholder="Leave blank if not required"
            />
          </div>
        </div>

        <div className="flex items-center justify-between mt-4">
          <div className="flex items-center space-x-4">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="signalk_ssl"
                checked={formData.signalk_ssl || false}
                onChange={(e) => handleInputChange('signalk_ssl', e.target.checked)}
                className="h-4 w-4 text-marine-600 focus:ring-marine-500 border-gray-300 rounded"
              />
              <label htmlFor="signalk_ssl" className="ml-2 block text-sm" style={{color: '#cbd5e1'}}>
                Use HTTPS (SSL/TLS)
              </label>
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                id="signalk_enabled"
                checked={formData.signalk_enabled !== false}
                onChange={(e) => handleInputChange('signalk_enabled', e.target.checked)}
                className="h-4 w-4 text-marine-600 focus:ring-marine-500 border-gray-300 rounded"
              />
              <label htmlFor="signalk_enabled" className="ml-2 block text-sm" style={{color: '#cbd5e1'}}>
                Enable data collection
              </label>
            </div>
          </div>
          
          {formData.signalk_host && (
            <button
              type="button"
              onClick={() => testSignalKConnection()}
              className="px-4 py-2 text-sm border rounded-md hover:bg-gray-700 transition-colors flex items-center gap-2"
              style={{borderColor: '#475569', color: '#cbd5e1'}}
            >
              🧪 Test Connection
            </button>
          )}
        </div>
      </div>

      {/* Additional Connection Types Preview */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Remote Access */}
        <div className="bg-gray-800/50 border border-gray-600 rounded-lg p-4 opacity-75">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-lg">🌐</span>
            <h4 className="font-medium" style={{color: '#f8fafc'}}>Remote Access</h4>
            <span className="text-xs px-2 py-1 bg-blue-600 text-white rounded">Coming Soon</span>
          </div>
          <p className="text-sm" style={{color: '#cbd5e1'}}>
            Access your SignalK server from anywhere via port forwarding or external domain.
          </p>
        </div>

        {/* VPN Connection */}
        <div className="bg-gray-800/50 border border-gray-600 rounded-lg p-4 opacity-75">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-lg">🛡️</span>
            <h4 className="font-medium" style={{color: '#f8fafc'}}>VPN Connection</h4>
            <span className="text-xs px-2 py-1 bg-blue-600 text-white rounded">Coming Soon</span>
          </div>
          <p className="text-sm" style={{color: '#cbd5e1'}}>
            Secure connection through VPN for regular data collection and monitoring.
          </p>
        </div>
      </div>

      {/* Connection Management Info */}
      <div className="bg-green-900/30 border border-green-600 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <span className="text-green-400 text-lg">💡</span>
          <div>
            <h4 className="font-medium text-green-200 mb-2">Smart Connection Management</h4>
            <div className="text-sm text-green-300 space-y-1">
              <p>• <strong>Local Network:</strong> High-speed connection when on your boat (e.g., 192.168.5.217)</p>
              <p>• <strong>Remote Access:</strong> External monitoring through port forwarding (coming soon)</p>
              <p>• <strong>VPN Connection:</strong> Secure access for regular data collection (coming soon)</p>
              <p>• <strong>Automatic Failover:</strong> System switches to best available connection</p>
              <p>• <strong>Storage Management:</strong> Control which connections store data to manage database size</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderSection = () => {
    switch (activeSection) {
      case 'basic': return renderBasicInfo();
      case 'physical': return renderPhysicalSpecs();
      case 'electrical': return renderElectrical();
      case 'engine': return renderEngine();
      case 'details': return renderDetails();
      case 'ownership': return renderOwnership();
      case 'service': return renderService();
      case 'dates': return renderImportantDates();
      case 'seasons': return renderSeasons();
      default: return renderBasicInfo();
    }
  };

  return (
    <div className="fixed inset-0 overflow-y-auto h-full w-full z-50" style={{backgroundColor: 'rgba(15, 23, 42, 0.8)'}}>
      <div className="relative top-16 mx-auto p-5 w-full max-w-7xl shadow-lg rounded-md" style={{backgroundColor: '#1e293b', border: '1px solid #475569', maxHeight: '90vh'}}>
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-medium" style={{color: '#f8fafc'}}>{title}</h3>
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-white"
          >
            ✕
          </button>
        </div>

        {/* Section Navigation */}
        <div className="mb-6">
          <div className="flex flex-wrap gap-2">
            {sections.map((section) => {
              const Icon = section.icon;
              return (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    activeSection === section.id 
                      ? 'bg-marine-600 text-white' 
                      : 'bg-marine-100 text-marine-700 hover:bg-marine-200'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {section.name}
                </button>
              );
            })}
          </div>
        </div>

        {/* Form Content */}
        <div className="max-h-[60vh] overflow-y-auto">
          {renderSection()}
        </div>

        {/* Form Actions */}
        <div className="flex justify-end gap-3 mt-6 pt-4 border-t" style={{borderColor: '#475569'}}>
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium rounded-md border"
            style={{borderColor: '#475569', color: '#cbd5e1', backgroundColor: 'transparent'}}
          >
            Cancel
          </button>
          <button
            onClick={onSubmit}
            className="px-4 py-2 text-sm font-medium rounded-md text-white"
            style={{backgroundColor: '#3b82f6'}}
          >
            {title.includes('Edit') ? 'Update Vessel' : 'Create Vessel'}
          </button>
        </div>
      </div>
      {showSpecsModal && typeof specsModalEngineIndex === 'number' && (formData.engines || [])[specsModalEngineIndex] && (
        <div className="fixed inset-0 overflow-y-auto" style={{backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 9999}} onClick={() => setShowSpecsModal(false)}>
          <div className="min-h-screen flex items-start justify-center p-4 sm:p-8" style={{marginTop: '5rem'}} onClick={(e) => e.stopPropagation()}>
            <div className="w-full max-w-3xl rounded-lg" style={{backgroundColor: '#0b1220', border: '1px solid #1f2a44', maxHeight: 'calc(100vh - 8rem)', overflowY: 'auto'}}>
              <div className="p-3 flex items-center justify-between sticky top-0" style={{borderBottom: '1px solid #1f2a44', backgroundColor: '#0b1220', zIndex: 2}}>
                <div className="text-sm font-medium" style={{color: '#cbd5e1'}}>Engine Specifications</div>
                <button aria-label="Close" className="text-xs px-2 py-1" style={{backgroundColor: '#334155', color: '#cbd5e1', borderRadius: '6px'}} onClick={() => setShowSpecsModal(false)}>✕</button>
              </div>
              <div className="p-4">
                {(() => {
                  const engine = (formData.engines || [])[specsModalEngineIndex] || {};
                  const specs = engine.specs || {};
                  const renderField = (label, value) => {
                    if (value === null || value === undefined || value === '' || (typeof value === 'object' && Object.keys(value).length === 0)) return null;
                    return (
                      <div className="flex justify-between text-xs py-0.5">
                        <div className="opacity-75">{label}</div>
                        <div className="font-medium" style={{color:'#e5e7eb'}}>{String(value)}</div>
                      </div>
                    );
                  };
                  return (
                    <div className="space-y-4 text-sm" style={{color: '#cbd5e1'}}>
                      {/* Basic */}
                      <div className="rounded-md p-3" style={{backgroundColor:'#0a1626', border:'1px solid #1f2a44'}}>
                        <div className="font-medium mb-2" style={{color: '#93c5fd'}}>Basic</div>
                        {renderField('Year', specs.basic?.year)}
                        {renderField('Make', specs.basic?.make)}
                        {renderField('Model', specs.basic?.model)}
                        {renderField('Serial', specs.basic?.serial)}
                        {renderField('Type', specs.basic?.type)}
                        {renderField('Fuel', specs.basic?.fuel)}
                        {renderField('Induction', specs.basic?.induction)}
                        {renderField('Cylinders', specs.basic?.cylinders)}
                      </div>
                      {/* Power */}
                      <div className="rounded-md p-3" style={{backgroundColor:'#0a1626', border:'1px solid #1f2a44'}}>
                        <div className="font-medium mb-2" style={{color: '#93c5fd'}}>Power</div>
                        {renderField('Horsepower', specs.power?.hp)}
                        {renderField('kW', specs.power?.kW)}
                        {renderField('Max RPM', specs.power?.maxRPM)}
                        {renderField('Torque (Nm)', specs.power?.torque_Nm)}
                      </div>
                      {/* Fluids */}
                      <div className="rounded-md p-3" style={{backgroundColor:'#0a1626', border:'1px solid #1f2a44'}}>
                        <div className="font-medium mb-2" style={{color: '#93c5fd'}}>Fluids</div>
                        {renderField('Oil capacity (L)', specs.fluids?.oil_capacity_L)}
                        {renderField('Recommended oil', specs.fluids?.recommended_oil)}
                        {renderField('Oil filter', specs.fluids?.oil_filter?.part)}
                        {renderField('Coolant type', specs.fluids?.coolant_type)}
                        {renderField('Gear oil', specs.fluids?.gear_oil)}
                      </div>
                      {Array.isArray((specs.parts)) && specs.parts.length > 0 && (
                        <div>
                          <div className="font-medium" style={{color: '#93c5fd'}}>Parts</div>
                          <div className="space-y-1">
                            {specs.parts.map((p, i) => (
                              <div key={i} className="border-b pb-1" style={{borderColor: '#1f2a44'}}>
                                <div className="font-medium">
                                  {p.url ? (
                                    <a href={p.url} target="_blank" rel="noopener noreferrer" className="hover:underline" style={{color: '#60a5fa'}}>
                                      {p.name}
                                    </a>
                                  ) : (
                                    <span>{p.name}</span>
                                  )}
                                  {p.part_number ? ` (${p.part_number})` : ''}
                                </div>
                                {p.category && <div className="opacity-75">{p.category}</div>}
                                {p.notes && <div className="opacity-75">{p.notes}</div>}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {Array.isArray(engine.parts_search_history) && engine.parts_search_history.length > 0 && (
                        <div>
                          <div className="font-medium" style={{color: '#93c5fd'}}>Search History</div>
                          <div className="space-y-2">
                            {engine.parts_search_history.map((h, i) => (
                              <div key={i} className="text-xs">
                                <div>“{h.query}” • {new Date(h.searched_at).toLocaleString()}</div>
                                <div className="mt-1">
                                  {(h.parts || []).slice(0,5).map((p, j) => (
                                    <span key={j} className="mr-2">
                                      {p.url ? (
                                        <a href={p.url} target="_blank" rel="noopener noreferrer" className="hover:underline" style={{color: '#60a5fa'}}>
                                          {p.name}
                                        </a>
                                      ) : (
                                        <span>{p.name}</span>
                                      )}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>
      )}
      {showHoursBrowser && formData.id !== undefined && (
        <div className="fixed inset-0 z-50" style={{backgroundColor: 'rgba(0,0,0,0.6)'}} onClick={() => setShowHoursBrowser(false)}>
          <div className="mx-auto mt-20 max-w-3xl px-4" onClick={(e) => e.stopPropagation()}>
            <div className="rounded-lg" style={{backgroundColor: '#0b1220', border: '1px solid #1f2a44'}}>
              <div className="p-3 flex items-center justify-between" style={{borderBottom: '1px solid #1f2a44'}}>
                <div className="text-sm font-medium" style={{color: '#cbd5e1'}}>Browse SignalK for Engine Hours</div>
                <button className="text-xs px-2 py-1" style={{backgroundColor: '#334155', color: '#cbd5e1', borderRadius: '6px'}} onClick={() => setShowHoursBrowser(false)}>Close</button>
              </div>
              <HoursDataBrowser
                vesselId={formData.id}
                onSelect={(path, value) => {
                  setFormData(prev => {
                    const next = { ...prev };
                    next.engines = [...(prev.engines || [])];
                    next.engines[hoursBrowserEngineIndex] = {
                      ...(next.engines[hoursBrowserEngineIndex] || {}),
                      hours_path: path,
                      hours: (() => {
                        const seconds = typeof value === 'number' ? value : parseFloat(value);
                        const hours = seconds / 3600;
                        return Math.round(hours * 10) / 10;
                      })()
                    };
                    return next;
                  });
                  setShowHoursBrowser(false);
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ComprehensiveVesselForm;

// Inline helper component to provide a uniform DataBrowser with data source toggle
const HoursDataBrowser = ({ vesselId, onSelect }) => {
  const [source, setSource] = React.useState('database');
  return (
    <div style={{height: '560px', display: 'flex', flexDirection: 'column'}}>
      <div style={{ display: 'flex', gap: '0.5rem', padding: '0.5rem 0.75rem', borderBottom: '1px solid #1f2a44' }}>
        {['database','live','demo'].map(id => (
          <button key={id}
            onClick={() => setSource(id)}
            style={{
              padding: '6px 10px',
              fontSize: '12px',
              borderRadius: '6px',
              border: '1px solid #334155',
              background: source === id ? '#3b82f6' : '#0f172a',
              color: source === id ? '#ffffff' : '#cbd5e1'
            }}
          >{id === 'database' ? 'Database' : id === 'live' ? 'Live' : 'Demo'}</button>
        ))}
      </div>
      <div style={{flex: 1, minHeight: 0}}>
        <DataBrowser
          vesselId={vesselId}
          dataSource={source}
          onPathSelect={(path, data) => {
            const value = data?.value ?? data;
            if (onSelect) onSelect(path, value);
          }}
          selectedPaths={[]}
        />
      </div>
    </div>
  );
};
